import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { GoHeartFill } from "react-icons/go";
import { BiSolidMessageSquareDetail } from "react-icons/bi";
import { IoBookmark } from "react-icons/io5";
import { FaShare } from 'react-icons/fa';
import { doc, updateDoc, arrayUnion, arrayRemove, onSnapshot } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';
import { FaUser } from 'react-icons/fa';
import CommentSection from './CommentSection';
import Modal from 'react-modal'; // Import react-modal

// Set the app element for accessibility (required by react-modal)
Modal.setAppElement('#root'); // Adjust '#root' to your app's root element ID

export default function MovieItem({ movie }) {
    const auth = getAuth();
    const navigate = useNavigate();
    const [isLiked, setIsLiked] = useState(movie.likedBy?.includes(auth.currentUser?.uid) || false);
    const [isSaved, setIsSaved] = useState(movie.savedBy?.includes(auth.currentUser?.uid) || false);
    const [likeCount, setLikeCount] = useState(movie.likes || 0);
    const [liveViewers, setLiveViewers] = useState(movie.currentViewers || 0);
    const [imageError, setImageError] = useState({
        poster: false,
        actor: false
    });
    const [likeAnimation, setLikeAnimation] = useState(false);
    const [saveAnimation, setSaveAnimation] = useState(false);
    const [showComments, setShowComments] = useState(false); // State for showing comments

    // Real-time listener for live viewers count
    useEffect(() => {
    if (!movie.id || !auth.currentUser) return;

    const movieRef = doc(db, 'movies', movie.id);
    const unsubscribe = onSnapshot(movieRef, (docSnap) => {
        if (docSnap.exists()) {
            const data = docSnap.data();

            setLiveViewers(data.currentViewers || "LIVE");
            setLikeCount(data.likes || 0);
            setIsLiked(data.likedBy?.includes(auth.currentUser.uid) || false);
            setIsSaved(data.savedBy?.includes(auth.currentUser.uid) || false);
        }
    }, (error) => {
        console.error("Error in MovieItem onSnapshot:", error);
    });

    return () => unsubscribe();
}, [movie.id, auth.currentUser]);


    const handleLike = async (e) => {
        e.stopPropagation();
        if (!auth.currentUser) {
            toast.error("Please sign in to like movies");
            return;
        }

        const movieRef = doc(db, 'movies', movie.id);
        const userId = auth.currentUser.uid;

        try {
            setLikeAnimation(true);
            setTimeout(() => setLikeAnimation(false), 1000);
            
            if (isLiked) {
                await updateDoc(movieRef, {
                    likedBy: arrayRemove(userId),
                    likes: likeCount - 1
                });
                setLikeCount(likeCount - 1);
                setIsLiked(false);
            } else {
                await updateDoc(movieRef, {
                    likedBy: arrayUnion(userId),
                    likes: likeCount + 1
                });
                setLikeCount(likeCount + 1);
                setIsLiked(true);
            }
        } catch (error) {
            toast.error("Error updating like status");
            console.error("Like error:", error);
        }
    };

    const handleSave = async (e) => {
        e.stopPropagation();
        if (!auth.currentUser) {
            toast.error("Please sign in to save movies");
            return;
        }

        const movieRef = doc(db, 'movies', movie.id);
        const userId = auth.currentUser.uid;

        try {
            setSaveAnimation(true);
            setTimeout(() => setSaveAnimation(false), 1000);
            
            if (isSaved) {
                await updateDoc(movieRef, {
                    savedBy: arrayRemove(userId)
                });
                setIsSaved(false);
            } else {
                await updateDoc(movieRef, {
                    savedBy: arrayUnion(userId)
                });
                setIsSaved(true);
            }
        } catch (error) {
            toast.error("Error updating save status");
            console.error("Save error:", error);
        }
    };

    const handleComment = (e) => {
        e.stopPropagation();
        if (!auth.currentUser) {
            toast.error("Please sign in to comment");
            return;
        }
        setShowComments(!showComments); // Toggle comment section
    };

    const handleShare = async (e) => {
        e.stopPropagation();
        
        // Construct the shareable URL for the movie
        const shareUrl = `${window.location.origin}/movie/${movie.id}`;
        const shareData = {
            title: movie.title,
            text: `Check out "${movie.title}" on AweitFlix! Starring ${movie.mainActorName || "an amazing cast"}.`,
            url: shareUrl,
        };

        try {
            // Check if the Web Share API is supported
            if (navigator.share) {
                await navigator.share(shareData);
            } else {
                // Fallback: Copy the URL to clipboard if Web Share API is not supported
                await navigator.clipboard.writeText(shareUrl);
                toast.info('Movie link copied to clipboard! Share it manually.');
            }
        } catch (error) {
            console.error('Error sharing movie:', error);
            // Fallback in case of error: Copy the URL to clipboard
            try {
                await navigator.clipboard.writeText(shareUrl);
                toast.info('Movie link copied to clipboard! Share it manually.');
            } catch (clipboardError) {
                console.error('Error copying to clipboard:', clipboardError);
                toast.error('Failed to share movie. Please share the link manually.');
            }
        }
    };

    const handleMovieClick = () => {
        if (!auth.currentUser) {
            toast.error("Please sign in to view movie details");
            navigate('/welcome');
            return;
        }
        navigate(`/movie/${movie.id}`);
    };

    const handleImageError = (type) => {
        setImageError(prev => ({ ...prev, [type]: true }));
    };

    if (!movie.id) {
        throw new Error("MovieItem requires a movie object with an 'id' property");
    }

    return (
        <div className={`relative group p-1 ${movie.isFeatured ? 'featured-movie' : ''}`}>
            <style jsx>{`
                .featured-movie {
                    position: relative;
                    background: linear-gradient(45deg, #FF1493, #9400D3, #FF8C00);
                    padding: 2px;
                    border-radius: 0.5rem;
                    box-shadow: 0 4px 20px rgba(255, 20, 147, 0.3);
                }
                
                .featured-movie .movie-container {
                    background: #000;
                    border-radius: 0.375rem;
                }
                
                .premium-badge {
                    position: absolute;
                    top: 8px;
                    right: 8px;
                    background: linear-gradient(45deg, #FF1493, #9400D3, #FF8C00);
                    color: white;
                    font-size: 10px;
                    font-weight: bold;
                    padding: 2px 8px;
                    border-radius: 9999px;
                    z-index: 10;
                    text-transform: uppercase;
                    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
                }
                
                .rating-badge {
                    position: absolute;
                    top: 8px;
                    left: 8px;
                    background: rgba(0, 0, 0, 0.7);
                    color: white;
                    font-size: 10px;
                    font-weight: bold;
                    padding: 2px 6px;
                    border-radius: 4px;
                    z-index: 10;
                    text-transform: uppercase;
                }

                @keyframes likeBounce {
                    0%, 100% { transform: scale(1); }
                    25% { transform: scale(1.2); }
                    50% { transform: scale(0.9); }
                    75% { transform: scale(1.1); }
                }

                @keyframes saveBounce {
                    0%, 100% { transform: scale(1); }
                    25% { transform: scale(1.2) rotate(-10deg); }
                    50% { transform: scale(0.9); }
                    75% { transform: scale(1.1) rotate(10deg); }
                }
            `}</style>

            {/* Premium Badge for Featured Movies */}
            {movie.isFeatured && (
                <div className="premium-badge">Featured</div>
            )}
            
            {/* Parental Guidance Rating Badge */}
            <div className="rating-badge">
                {movie.contentRating || 'PG'}
            </div>

            <div 
                className={`w-full h-full min-h-[180px] bg-black rounded-md overflow-hidden shadow-sm hover:shadow-md transition-all duration-200 group flex flex-col cursor-pointer ${movie.isFeatured ? 'movie-container' : ''}`}
            >
                {/* Poster Image - Clickable area for movie navigation */}
                <div 
                    className="flex-1 relative overflow-hidden"
                    onClick={handleMovieClick}
                >
                    {imageError.poster ? (
                        <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                            <span className="text-gray-900"></span>
                        </div>
                    ) : (
                        <img 
                            src={movie.posterUrl} 
                            alt={movie.title}
                            className="w-full h-full object-cover group-hover:opacity-75 transition-opacity duration-200"
                            loading="lazy"
                            decoding="async"
                            onError={() => handleImageError('poster')}
                        />
                    )}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                        <span className="text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            View Movie
                        </span>
                    </div>

                    {/* Live Viewers Count */}
                    <div className="absolute bottom-2 left-2 bg-gray-700 bg-opacity-70 rounded-full px-2 py-1 flex items-center space-x-1 z-10">
                        <FaUser className="text-white text-xs" />
                        <span className="text-white text-xs font-medium">
                            {liveViewers >= 1000 ? `${(liveViewers / 1000).toFixed(1)}K` : liveViewers}
                        </span>
                    </div>
                </div>

                {/* Movie Details */}
                <div className="p-2 flex flex-col items-center text-white">
                    {/* Title - Clickable area for movie navigation */}
                    <h3 
                        className="text-xs sm:text-[11px] uppercase truncate w-full text-center font-medium"
                        onClick={handleMovieClick}
                    >
                        {movie.title}
                    </h3>
                    
                    {/* Main Actor and Release Year - Clickable area for movie navigation */}
                    <div 
                        className="flex items-center space-x-2 mt-1"
                        onClick={handleMovieClick}
                    >
                        {movie.actorImageUrl ? (
                            imageError.actor ? (
                                <div className="w-6 h-6 rounded-full bg-gray-700 flex items-center justify-center">
                                    <span className="text-gray-900"></span>
                                </div>
                            ) : (
                                <img
                                    src={movie.actorImageUrl}
                                    alt={movie.mainActorName || "Main Actor"}
                                    className="w-6 h-6 rounded-full object-cover"
                                    onError={() => handleImageError('actor')}
                                />
                            )
                        ) : (
                            <div className="w-6 h-6 rounded-full bg-gray-700 flex items-center justify-center">
                                <span className="text-gray-900"></span>
                            </div>
                        )}
                        <div className="flex flex-col">
                            <p className="text-[10px] sm:text-[9px] text-gray-400 truncate">
                                {movie.mainActorName || "Unknown Actor"}
                            </p>
                            <p className="text-[10px] sm:text-[9px] text-gray-400 truncate">
                                {movie.releaseYear}
                            </p>
                        </div>
                    </div>
                    
                    {/* Actions - Non-clickable area for movie navigation */}
                    <div 
                        className="w-full bg-gray-800 mt-2 py-1 flex justify-around items-center rounded"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button 
                            onClick={handleLike}
                            className="flex items-center hover:text-[#FF1493] transition-colors duration-200"
                        >
                            <GoHeartFill 
                                size={17} 
                                className={`${isLiked ? "text-[#FF1493]" : "text-white"} ${likeAnimation ? "animate-[likeBounce_1s_ease-in-out]" : ""}`} 
                            />
                        </button>
                        <button 
                            onClick={handleComment}
                            className="flex items-center hover:text-gray-300 transition-colors duration-200"
                        >
                            <BiSolidMessageSquareDetail size={17} className={`text-white ${showComments ? 'text-pink-500' : ''}`} />
                        </button>
                        <button 
                            onClick={handleSave}
                            className="flex items-center hover:text-[#1fa9ff] transition-colors duration-200"
                        >
                            <IoBookmark 
                                size={17} 
                                className={`${isSaved ? "text-[#1fa9ff]" : "text-white"} ${saveAnimation ? "animate-[saveBounce_1s_ease-in-out]" : ""}`} 
                            />
                        </button>
                        <button 
                            onClick={handleShare}
                            className="flex items-center hover:text-gray-300 transition-colors duration-200"
                        >
                            <FaShare size={17} className="text-white" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Comment Section in Modal */}
            <Modal
                isOpen={showComments}
                onRequestClose={() => setShowComments(false)}
                style={{
                    content: {
                        top: '50%',
                        left: '50%',
                        right: 'auto',
                        bottom: 'auto',
                        marginRight: '-50%',
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: '#1a1a1a',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '20px',
                        maxHeight: '80vh',
                        overflowY: 'auto',
                        width: '90%',
                        maxWidth: '500px',
                    },
                    overlay: {
                        backgroundColor: 'rgba(0, 0, 0, 0.75)',
                        zIndex: 1000,
                    },
                }}
                contentLabel="Comment Modal"
            >
                <CommentSection contentId={movie.id} contentType="movie" />
                <button
                    onClick={() => setShowComments(false)}
                    className="mt-4 px-4 py-2 bg-pink-500 text-white rounded hover:bg-pink-600"
                >
                    Close
                </button>
            </Modal>
        </div>
    );
}